/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import InputV1 from '../../../../../../ComponentsV1/InputV1';
import { WFHCall } from '../../../../../../commons';
import './animation.css';
import {
  CalledTo,
  CallEntity,
  DropdownOptionIcon,
  JumboEventFunction,
} from '../constants';
import { useState } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { merchantModel } from '../../../../../../../api';
import { useWidgetAnimation } from './useWidgetAnimation';

const CallingWidget = ({
  enabled = false,
  is_skipped: isSkipped = false,
  call_status = {},
  callingTo = 'SM',
  response_options: responseOptions = [],
  default_option: defaultValue = {},
  orderId = null,
  userId = null,
  cartId = null,
  updateCallStatus = () => {},
  onCallInitiated = () => {},
  isStateUpdating = false,
}) => {
  const isDisabled = !enabled;
  const animClass = useWidgetAnimation(
    callingTo === 'SM' ? 'widget-animate-1' : 'widget-animate-2',
  );
  const [latestResponse, setLatestResponse] = useState(defaultValue?.label);

  const [displayedStatus, setDisplayedStatus] = useState(call_status?.text);
  const [exitingStatus, setExitingStatus] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [calledToType, setCalledToType] = useState({
    SM: 'Assistant Store Manager',
    DP: 'Rider',
  });

  const prevStatusRef = useRef({
    text: call_status?.text,
    color: call_status?.text_color,
  });

  useEffect(() => {
    setLatestResponse(defaultValue?.label);
  }, [defaultValue]);

  useEffect(() => {
    const getMerchantInfo = () => {
      if (!orderId) return;
      let queryStrings = { order_id: orderId };
      merchantModel.getMerchantData(queryStrings).then((response) => {
        if (!response || response?.isError === true) return;
        const {
          sm_details: smDetails,
          tl_details: tlDetails,
          asm_details: asmDetails,
        } = response || {};
        const updatedRole =
          smDetails?.role || asmDetails?.role || tlDetails?.role;

        if (updatedRole) {
          setCalledToType((prev) => ({
            ...prev,
            SM: updatedRole,
          }));
        }
      });
    };

    getMerchantInfo();
  }, [orderId]);

  useEffect(() => {
    const newText = call_status?.text;
    const newColor = call_status?.text_color;

    if (newText !== prevStatusRef.current.text) {
      setExitingStatus(prevStatusRef.current);
      setDisplayedStatus(newText);
      setAnimationKey((k) => k + 1);

      prevStatusRef.current = { text: newText, color: newColor };

      const timer = setTimeout(() => {
        setExitingStatus(null);
      }, 350);

      return () => clearTimeout(timer);
    }

    // update color even if text didn't change
    prevStatusRef.current = {
      ...prevStatusRef.current,
      color: newColor,
    };
  }, [call_status?.text, call_status?.text_color]);

  return (
    <div
      className={`tw-flex tw-flex-col ${animClass} ${
        isStateUpdating ? 'tw-pointer-events-none !tw-opacity-60' : ''
      }`}
    >
      <div
        className={`tw-flex-col tw-border-solid tw-border-[1px] tw-border-grey-200 tw-rounded-[8px]  tw-p-3 tw-gap-3 tw-bg-grey-100/30 tw-w-full ${
          isDisabled ? '!tw-cursor-not-allowed tw-pointer-events-none' : ''
        }`}
      >
        <div
          className={`tw-flex tw-h-16 tw-gap-3 ${
            isSkipped ? 'tw-items-center' : ''
          }`}
        >
          {/* Callee Icon */}
          <div
            className={`tw-aspect-square ${
              isDisabled
                ? 'tw-bg-grey-100  tw-border-grey-200/50 tw-text-zinc-400'
                : 'tw-bg-teal-100  tw-border-teal-200/50'
            } tw-rounded-[6px] tw-justify-center tw-items-center tw-w-16 tw-h-16 tw-border-solid tw-border-[1px]`}
          >
            {CallEntity[callingTo].icon}
          </div>

          {/* Callee header and call status */}
          <div className='tw-flex-1 tw-flex tw-flex-col tw-leading-none tw-justify-between'>
            {/* Callee header */}
            <div
              className={`tw-text-[1.5rem] tw-font-medium ${
                isSkipped ? '' : 'tw-mt-[0.2rem]'
              } ${isDisabled ? 'tw-text-zinc-400' : 'tw-text-zinc-600'}`}
            >
              {CallEntity[callingTo].header}
            </div>

            {/* Call status */}
            {!isSkipped && (
              <div className='status-text-container tw-font-medium tw-text-[1.3rem]'>
                {exitingStatus && (
                  <div
                    key={`exit-${animationKey}`}
                    className='status-text-exit !tw-whitespace-nowrap'
                    style={{
                      color: exitingStatus.color || '#71717a',
                    }}
                  >
                    {exitingStatus.text}
                  </div>
                )}
                <div
                  key={`enter-${animationKey}`}
                  className={
                    animationKey > 0 ? 'status-text-enter tw-mt-1' : 'tw-mt-1'
                  }
                  style={{
                    color: isDisabled
                      ? '#71717a'
                      : call_status?.text_color || '#71717a',
                  }}
                >
                  {displayedStatus}
                </div>
              </div>
            )}
          </div>

          {/* Calling button */}
          {!isSkipped && (
            <div
              className='tw-flex tw-items-center tw-justify-center tw-h-16 tw-w-16 tw-rounded-full hover:tw-bg-green-600/10 tw-transition-all tw-ease-out tw-duration-200 tw-cursor-pointer'
              data-dd-action-name='Reso Assist Call'
            >
              <WFHCall
                orderId={orderId}
                isCallingDisabled={isDisabled}
                userId={userId}
                cartId={cartId}
                showTooltip={false}
                pos='bottom'
                isUser={true}
                isAltContact={false}
                isNewUser={false}
                sendJumboEvent={true}
                jumboEventFunc={JumboEventFunction[callingTo]}
                calledTo={CalledTo[callingTo]}
                calledToType={calledToType[callingTo]}
                altContactMissing={true}
                JumboEventProperties={{
                  called_to: CalledTo[callingTo],
                  is_new_ui: true,
                }}
                isResoAssistFlow={true}
                onCallSuccess={() => onCallInitiated(callingTo)}
              />
            </div>
          )}

          {/* Skipped Tag */}
          {isSkipped && (
            <div className='tw-text-[1.3rem] tw-text-zinc-500 tw-font-medium tw-rounded-lg tw-bg-grey-200/80 tw-px-2 tw-py-[0.4rem] tw-whitespace-nowrap tw-leading-none tw-w-fit tw-ml-2'>
              Skipped
            </div>
          )}
        </div>

        {/* Status Dropdown */}
        {!isSkipped && (
          <InputV1
            showArrows={true}
            searchIcon={(() => {
              const Icon = DropdownOptionIcon[defaultValue?.value];
              return Icon ? (
                <Icon size={16} className='tw-text-zinc-600' />
              ) : null;
            })()}
            renderOption={(opt) => (
              <div className='tw-flex tw-items-center tw-gap-3'>
                {(() => {
                  const Icon = DropdownOptionIcon[opt?.value];
                  return Icon ? (
                    <Icon size={16} className='tw-text-zinc-600' />
                  ) : null;
                })()}
                <span className='tw-font-medium tw-leading-none'>
                  {opt?.label}
                </span>
              </div>
            )}
            disabled={isDisabled}
            inputClassName='tw-py-0 !tw-h-[4rem] !tw-min-h-[2rem] !tw-border-grey-200/90 !tw-flex !tw-flex-nowrap !tw-items-center'
            inputProps={{
              className:
                'placeholder:tw-text-[1.3rem] placeholder:tw-font-regular placeholder:tw-text-zinc-400 tw-w-full   tw-border-none tw-font-medium !tw-text-base-green',
            }}
            enableKeyPress={true}
            options={responseOptions}
            placeholder={'What did they say?'}
            data-dd-action-name='Reso Assist Call Response'
            multiple={false}
            isSearchable={true}
            style={{ width: '100%' }}
            closeOnSelect
            onChange={(val) => {
              if (val === latestResponse) {
                return;
              }
              updateCallStatus(callingTo, val);
              setLatestResponse(val || '');
            }}
            value={latestResponse}
          />
        )}
      </div>

      {/* Vertical Separator */}
      <div
        style={{
          width: '0.2rem',
          height: '2.5rem',
          backgroundColor: '#e5e7eb',
          marginLeft: '1.5rem',
          transformOrigin: 'top',
          animation: 'growLine 600ms ease-out forwards',
        }}
      />
    </div>
  );
};

export default CallingWidget;
